import React, { useState, useEffect, memo } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import Alert from '@cloudscape-design/components/alert'
import ProgressBar from '@cloudscape-design/components/progress-bar'
import Container from '@cloudscape-design/components/container'

/**
 * ImportDialog component for previewing and confirming CSV import
 */
const ImportDialog = memo(({ 
  isOpen, 
  onClose, 
  onConfirm, 
  recordCount, 
  errors, 
  isProcessing 
}) => {
  const [progress, setProgress] = useState(0)

  // Simulate progress for large file imports
  useEffect(() => {
    if (isProcessing && recordCount > 1000) {
      setProgress(0)
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval)
            return 90
          }
          return prev + 10
        })
      }, 500)
      return () => clearInterval(interval)
    } else if (isProcessing) {
      setProgress(50)
    } else {
      setProgress(0)
    }
  }, [isProcessing, recordCount])

  const hasErrors = errors && errors.length > 0
  const isLargeFile = recordCount > 1000

  return (
    <Modal
      visible={isOpen}
      onDismiss={onClose}
      header={hasErrors ? 'Import Validation Failed' : 'Confirm CSV Import'}
      footer={
        !isProcessing && (
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClose}>
                {hasErrors ? 'Close' : 'Cancel'}
              </Button>
              {!hasErrors ? (
                <Button variant="primary" onClick={onConfirm}>
                  Confirm Import
                </Button>
              ) : errors.length > 0 && errors[0].column === 'Import' ? (
                <Button variant="primary" onClick={onConfirm}>
                  Retry Import
                </Button>
              ) : null}
            </SpaceBetween>
          </Box>
        )
      }
    >
      <SpaceBetween size="l">
        {isProcessing ? (
          <Container>
            <SpaceBetween size="m">
              {isLargeFile ? (
                <>
                  <ProgressBar
                    value={progress}
                    label="Processing import"
                    description={`Processing ${recordCount} records...`}
                    additionalInfo="This may take a moment for large files."
                  />
                </>
              ) : (
                <Box textAlign="center">
                  <ProgressBar value={50} label="Processing import" />
                </Box>
              )}
            </SpaceBetween>
          </Container>
        ) : hasErrors ? (
          <SpaceBetween size="m">
            <Box variant="p">
              The CSV file contains validation errors that must be fixed before importing:
            </Box>
            <Alert type="error" header="Validation Errors">
              <SpaceBetween size="xs">
                {errors.map((error, index) => (
                  <Box key={index}>
                    <Box variant="strong">Row {error.row}:</Box> {error.message}
                    {error.column && ` (Column: ${error.column})`}
                    {error.value && (
                      <Box variant="code" fontSize="body-s" padding={{ top: 'xxs' }}>
                        Value: "{error.value}"
                      </Box>
                    )}
                  </Box>
                ))}
              </SpaceBetween>
            </Alert>
          </SpaceBetween>
        ) : (
          <SpaceBetween size="m">
            <Alert type="success" header="Ready to import">
              <strong>{recordCount} record{recordCount !== 1 ? 's' : ''}</strong> will be added to the database.
            </Alert>
            
            {isLargeFile && (
              <Alert type="warning" header="Large file detected">
                This file contains over 1,000 records. Import may take several seconds to complete.
              </Alert>
            )}
            
            <Box variant="p">
              Click "Confirm Import" to add these records to the database. This action cannot be undone.
            </Box>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  )
})

ImportDialog.displayName = 'ImportDialog'

export default ImportDialog
